import {
  Bell,
  Boxes,
  CircleHelp,
  CircleDollarSign,
  Gauge,
  Headphones,
  Heart,
  Home,
  MessageSquare,
  PackageCheck,
  RefreshCw,
  Send,
  ShieldCheck,
  Sparkles,
  Ticket,
  Truck,
  WalletCards,
} from "lucide-react";

export const navItems = [
  { href: "/", label: "Home" },
  { href: "/product", label: "Product Preview" },
  { href: "/cart", label: "Cart" },
  { href: "/user-center", label: "User Center" },
  { href: "/storage", label: "Storage" },
  { href: "/user-center/parcels", label: "Parcels" },
  { href: "/user-center/wallet", label: "Wallet" },
  { href: "/partner", label: "Partner" },
  { href: "/admin", label: "Admin" },
];

export const agentPlatformLinks = [
  { label: "Taobao", href: "https://www.taobao.com/", logo: "/images/footer/agent-taobao.svg", tone: "taobao" },
  { label: "1688", href: "https://www.1688.com/", logo: "/images/footer/agent-1688.svg", tone: "china1688" },
  { label: "Weidian", href: "https://weidian.com/official/pc/", logo: "/images/footer/agent-weidian.svg", tone: "weidian" },
  { label: "Yupoo", href: "https://www.yupoo.com/", logo: "/images/footer/agent-yupoo.svg", tone: "yupoo" },
] as const;

export type SocialPlatform =
  | "Telegram"
  | "Discord"
  | "Reddit"
  | "YouTube"
  | "TikTok"
  | "Instagram"
  | "WhatsApp";

export const socialLinks: ReadonlyArray<{
  label: SocialPlatform;
  href: string | null;
  logo: string;
  tone: string;
}> = [
  { label: "Telegram", href: null, logo: "/images/footer/social-telegram.svg", tone: "telegram" },
  { label: "Discord", href: null, logo: "/images/footer/social-discord.svg", tone: "discord" },
  { label: "Reddit", href: null, logo: "/images/footer/social-reddit.svg", tone: "reddit" },
  {
    label: "YouTube",
    href: "https://www.youtube.com/@PRVBUY",
    logo: "/images/footer/social-youtube.svg",
    tone: "youtube",
  },
  { label: "TikTok", href: null, logo: "/images/footer/social-tiktok.svg", tone: "tiktok" },
  { label: "Instagram", href: null, logo: "/images/footer/social-instagram.svg", tone: "instagram" },
  {
    label: "WhatsApp",
    href: "https://wa.me/message/LUAABZMJY5HMJ1",
    logo: "/images/footer/social-whatsapp.svg",
    tone: "whatsapp",
  },
];

export function getSocialLink(platform: SocialPlatform) {
  return socialLinks.find((link) => link.label === platform)!;
}

export type PortalNavItem = {
  href: string;
  label: string;
  icon: typeof Home;
  status?: "coming-soon";
  activePaths?: readonly string[];
  groupStart?: boolean;
};

export const portalNav: readonly PortalNavItem[] = [
  { href: "/user-center", label: "Home", icon: Home, activePaths: ["/dashboard"] },
  { href: "/user-center/wishlist", label: "Wish List", icon: Heart, groupStart: true },
  { href: "/user-center/orders", label: "Orders", icon: PackageCheck, activePaths: ["/user-center/order-detail", "/user-center/order-pay"] },
  { href: "/user-center/after-sales", label: "Refunds / After-Sales", icon: Ticket },
  { href: "/user-center/warehouse", label: "Warehouse", icon: Boxes, activePaths: ["/storage", "/submit-packing"] },
  { href: "/user-center/rehearsal", label: "Rehearsal", icon: RefreshCw },
  { href: "/user-center/parcels", label: "Parcels", icon: Truck, activePaths: ["/parcel", "/user-center/parcel-checkout"] },
  { href: "/user-center/wallet", label: "Wallet", icon: WalletCards, groupStart: true },
  { href: "/user-center/points", label: "Points", icon: CircleDollarSign },
  { href: "/user-center/coupons", label: "Coupons", icon: Ticket },
  { href: "/help-center", label: "Help Center", icon: CircleHelp, groupStart: true },
  { href: "/user-center/message", label: "Message", icon: MessageSquare },
  { href: "/user-center/support", label: "Support", icon: Headphones },
];

export function isPortalNavItemActive(
  pathname: string,
  item: Pick<PortalNavItem, "href" | "activePaths">,
) {
  const normalizedPath = pathname !== "/" ? pathname.replace(/\/$/, "") : pathname;
  return [item.href, ...(item.activePaths ?? [])].some((candidate) => {
    const normalizedCandidate = candidate !== "/" ? candidate.replace(/\/$/, "") : candidate;
    return normalizedPath === normalizedCandidate || normalizedPath.startsWith(`${normalizedCandidate}/`);
  });
}

export const product = {
  title: "Technical Shell Jacket, Graphite Edition",
  source: "1688 verified supplier",
  price: "¥429.00",
  stock: "284 available",
  eta: "Arrives at PRV Storage in 2-4 days",
  imageTone: "Graphite / Ice white",
};

export const pricingRows = [
  ["Product Price", "¥429.00"],
  ["Service Fee", "¥34.32"],
  ["Domestic Shipping", "¥8.00"],
  ["Estimated International Shipping", "¥156.00"],
  ["Payment Fee", "¥14.91"],
];

export const dashboardStats = [
  { label: "Wallet", value: "$1,284.20", delta: "+12.8%", icon: CircleDollarSign },
  { label: "Active Orders", value: "18", delta: "6 in QC", icon: PackageCheck },
  { label: "Storage Items", value: "42", delta: "11 ready", icon: Boxes },
  { label: "Parcels", value: "7", delta: "3 in transit", icon: Truck },
  { label: "Notifications", value: "12", delta: "4 priority", icon: Bell },
];

export const orderRows = [
  ["PB-10291", "Technical Shell Jacket", "QC", "12 photos ready", "CNY 642.23"],
  ["PB-10288", "Minimal Running Sneakers", "Stored", "Ready to consolidate", "CNY 388.40"],
  ["PB-10276", "Aluminum Desk Lamp", "Purchased", "Inbound to warehouse", "CNY 516.10"],
  ["PB-10271", "Merino Knit Set", "Pending", "Seller confirmation needed", "CNY 268.00"],
  ["PB-10264", "Graphite Carry Case", "Packed", "Parcel draft created", "CNY 188.00"],
];

export const announcementRows = [
  ["Route update", "DHL volumetric rules refreshed for US parcels."],
  ["Warehouse note", "QC photos now include label closeups by default."],
  ["Partner", "Affiliate payouts settle every Friday."],
];

export const storageItems = [
  {
    name: "Merino knit set",
    status: "QC approved",
    days: "8 days",
    weight: "1.2 kg",
    photos: "12 QC photos",
  },
  {
    name: "Aluminum desk lamp",
    status: "Measured",
    days: "3 days",
    weight: "2.8 kg",
    photos: "9 QC photos",
  },
  {
    name: "Graphite carry case",
    status: "Ready to ship",
    days: "14 days",
    weight: "0.7 kg",
    photos: "15 QC photos",
  },
  {
    name: "Tailored wool coat",
    status: "Photo retake",
    days: "1 day",
    weight: "1.6 kg",
    photos: "6 QC photos",
  },
];

export const parcelRows = [
  ["PRV-84391", "DHL Express", "New York", "In transit", "$42.18"],
  ["PRV-84377", "FedEx Priority", "London", "Label ready", "$38.44"],
  ["PRV-84206", "UPS Saver", "Toronto", "Customs cleared", "$29.12"],
  ["PRV-84188", "EMS Premium", "Berlin", "Delivered", "$24.80"],
];

export const walletRows = [
  ["Top-up", "Card payment", "+$750.00", "Completed"],
  ["Purchase", "Order PB-10291", "-$96.88", "Completed"],
  ["Shipping", "Parcel PRV-84391", "-$42.18", "Pending"],
  ["Reward", "Partner payout", "+$128.40", "Available"],
];

export const cartRows = [
  ["Technical Shell Jacket", "Graphite / L", "1", "¥642.23"],
  ["Merino knit set", "Bone / M", "2", "¥388.40"],
  ["Aluminum desk lamp", "Titanium silver", "1", "¥516.10"],
];

export const trustItems = [
  { title: "QC Photos", copy: "High-resolution inspection sets before shipment.", icon: ShieldCheck },
  { title: "PRV Storage", copy: "Consolidate, protect and manage items with clear status.", icon: Boxes },
  { title: "Tracking", copy: "Milestones from purchase to final delivery.", icon: Gauge },
  { title: "Shipping", copy: "Route options with transparent cost estimates.", icon: Send },
  { title: "Transparent Pricing", copy: "Every fee visible before checkout.", icon: Sparkles },
];

export const storySteps = [
  "Paste Link",
  "Purchase",
  "QC",
  "Storage",
  "Shipping",
  "Delivered",
];
